import { Box, Button, Typography, useTheme } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { AI_INTRO_COPY } from "../onboardingSteps";
import { BrandWordmark } from "../assets/BrandWordmark";
import { AiChatDemo } from "../assets/AiChatDemo";
import { TypewriterWords } from "../assets/TypewriterWords";
import { riseIn } from "../onboardingMotion";
import { displayTitleSx, primaryButtonSx, textBody } from "./stepStyles";

/**
 * The first slide proper: a live Insight AI demo answering a real question.
 * The demo LEADS and the headline narrates it, so the app proves what it does
 * before any claim is made about it.
 *
 * Carries the wordmark like the other text-led beats (commitment, mission,
 * honesty) so the brand stays anchored in the same place across the flow.
 */

interface AiIntroStepProps {
  onContinue: () => void;
}

export const AiIntroStep = ({ onContinue }: AiIntroStepProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const reduced = useReducedMotion();

  const rise = (delay: number) => ({
    variants: riseIn(reduced ? 0 : delay),
    initial: "hidden" as const,
    animate: "visible" as const,
  });

  return (
    <Box sx={{ pt: 1, flex: 1, display: "flex", flexDirection: "column", textAlign: "center" }}>
      {/* Same wordmark component as the other text-led beats, so the mark
          never resizes or shifts as the user taps through. */}
      <motion.div {...rise(0)}>
        <BrandWordmark />
      </motion.div>

      {/* The demo centres in whatever space is left above the copy block, so
          it stays the focal point on both short and tall screens. */}
      <Box
        component={motion.div}
        {...rise(0.08)}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AiChatDemo compact />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, pt: 2 }}>
        <motion.div {...rise(0.16)}>
          <Typography sx={{ ...displayTitleSx(isDark), fontSize: "1.875rem" }}>
            {AI_INTRO_COPY.titlePrefix} <TypewriterWords words={AI_INTRO_COPY.titleWords} />
          </Typography>
        </motion.div>
        <motion.div {...rise(0.22)}>
          <Typography
            sx={{
              fontSize: "0.9375rem",
              lineHeight: 1.5,
              color: textBody(isDark),
              maxWidth: 330,
              mx: "auto",
            }}
          >
            {AI_INTRO_COPY.body}
          </Typography>
        </motion.div>
      </Box>

      <motion.div {...rise(0.3)}>
        <Box sx={{ pt: 3 }}>
          <Button
            fullWidth
            onClick={onContinue}
            sx={{
              ...primaryButtonSx,
              transition: "background-color 0.15s ease, transform 0.1s ease",
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            {AI_INTRO_COPY.cta}
          </Button>
        </Box>
      </motion.div>
    </Box>
  );
};
